/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * Campaign Voice Settings Service - API layer for campaign voice configuration
 *
 * @fileoverview API service for campaign-level voice settings (GM-controlled)
 * @version 1.0.0
 * @since v0.0.08
 *
 * PURPOSE:
 * Provides API service layer for managing campaign voice policies that apply
 * to all sessions within a campaign. These are distinct from personal voice
 * settings (which players control individually).
 *
 * KEY RESPONSIBILITIES:
 * - Fetch campaign voice settings
 * - Update campaign voice settings (GM only)
 * - Graceful error handling with localStorage fallback
 *
 * BACKEND INTEGRATION:
 * - GET /api/campaigns/{id}/voice-settings
 * - PUT /api/campaigns/{id}/voice-settings
 *
 * CONTRACT ALIGNMENT:
 * - Agent_A_Voice_Bridge.md (API specifications)
 * - Campaign voice policies architecture
 *
 * CHANGE LOG:
 * 2026-01-05 - Agent A - Initial implementation
 */

import { apiConfig } from '../../config/api';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

/**
 * Campaign voice settings configuration
 */
export interface CampaignVoiceSettings {
  default_mode: 'transcribe_all' | 'wake_word' | 'off';
  mm_trigger_mode: 'transcribe_all' | 'wake_word' | 'turn_based' | 'smart';
  multi_speaker_mode: 'parallel' | 'sequential' | 'priority' | 'player_level';
  session_persistence: 'db_persistence' | 'ephemeral' | 'grace_period';
  relay_preference: 'always_on' | 'optional' | 'disabled';
}

/**
 * API response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  error: {
    code: number;
    message: string;
  } | null;
}

// ============================================================================
// DEFAULT SETTINGS
// ============================================================================

export const DEFAULT_VOICE_SETTINGS: CampaignVoiceSettings = {
  default_mode: 'transcribe_all',
  mm_trigger_mode: 'transcribe_all',
  multi_speaker_mode: 'parallel',
  session_persistence: 'grace_period',
  relay_preference: 'optional'
};

// ============================================================================
// SERVICE CLASS
// ============================================================================

/**
 * Campaign Voice Settings Service
 *
 * Provides API methods for campaign voice settings management with graceful
 * error handling. Implements localStorage fallback for development.
 */
class CampaignVoiceSettingsService {
  private baseUrl: string;
  private localStoragePrefix = 'campaign_voice_settings_';

  constructor() {
    this.baseUrl = apiConfig.baseUrl;
  }

  /**
   * Get voice settings for a campaign
   *
   * Fetches campaign-level voice policies from backend. Falls back to
   * localStorage if backend unavailable, then to defaults.
   *
   * @param campaignId - The campaign UUID
   * @returns ApiResponse containing CampaignVoiceSettings or error
   *
   * @throws Never throws - returns error in ApiResponse
   */
  async getSettings(campaignId: string): Promise<ApiResponse<CampaignVoiceSettings>> {
    try {
      const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}/voice-settings`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Backend endpoint not yet implemented');
      }

      const data: CampaignVoiceSettings = await response.json();
      console.log('[CampaignVoiceSettingsService] Loaded settings:', data);
      
      return {
        success: true,
        data,
        error: null
      };

    } catch (error) {
      console.warn('[CampaignVoiceSettingsService] Backend fetch failed, trying localStorage:', error);

      // Graceful fallback: Try localStorage
      const localSettings = this.getLocalSettings(campaignId);
      
      if (localSettings) {
        return {
          success: true,
          data: localSettings,
          error: null
        };
      }

      // Final fallback: Return defaults
      console.log('[CampaignVoiceSettingsService] Using default settings');
      return {
        success: true,
        data: DEFAULT_VOICE_SETTINGS,
        error: null
      };
    }
  }

  /**
   * Update voice settings for a campaign
   *
   * Updates campaign-level voice policies. Saves to backend, with localStorage
   * fallback if backend unavailable.
   *
   * @param campaignId - The campaign UUID
   * @param settings - New voice settings configuration
   * @returns ApiResponse containing updated settings or error
   *
   * AUTHORIZATION:
   * - Requires GM role (campaign creator)
   * - Returns 403 if not authorized
   *
   * @throws Never throws - returns error in ApiResponse
   */
  async updateSettings(
    campaignId: string,
    settings: CampaignVoiceSettings
  ): Promise<ApiResponse<CampaignVoiceSettings>> {
    try {
      const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}/voice-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        if (response.status === 403) {
          return {
            success: false,
            data: null,
            error: {
              code: 403,
              message: 'Only the GM can modify voice settings.'
            }
          };
        }

        throw new Error('Backend endpoint not yet implemented');
      }

      const data: CampaignVoiceSettings = await response.json();
      console.log('[CampaignVoiceSettingsService] Settings updated:', data);

      // Also save to localStorage as backup
      this.saveLocalSettings(campaignId, data);

      return {
        success: true,
        data,
        error: null
      };

    } catch (error) {
      console.warn('[CampaignVoiceSettingsService] Backend save failed, using localStorage:', error);

      // Graceful fallback: Save to localStorage
      this.saveLocalSettings(campaignId, settings);

      return {
        success: true,
        data: settings,
        error: null
      };
    }
  }

  /**
   * Get settings from localStorage
   *
   * @param campaignId - The campaign UUID
   * @returns Cached settings or null
   */
  private getLocalSettings(campaignId: string): CampaignVoiceSettings | null {
    try {
      const key = this.localStoragePrefix + campaignId;
      const cached = localStorage.getItem(key);

      if (cached) {
        const parsed = JSON.parse(cached);
        console.log('[CampaignVoiceSettingsService] Loaded from localStorage:', parsed);
        return parsed;
      }

      return null;

    } catch (error) {
      console.error('[CampaignVoiceSettingsService] localStorage read error:', error);
      return null;
    }
  }

  /**
   * Save settings to localStorage
   *
   * @param campaignId - The campaign UUID
   * @param settings - Settings to cache
   */
  private saveLocalSettings(campaignId: string, settings: CampaignVoiceSettings): void {
    try {
      const key = this.localStoragePrefix + campaignId;
      localStorage.setItem(key, JSON.stringify(settings));
      console.log('[CampaignVoiceSettingsService] Saved to localStorage');

    } catch (error) {
      console.error('[CampaignVoiceSettingsService] localStorage write error:', error);
    }
  }

  /**
   * Clear cached settings for a campaign
   *
   * @param campaignId - The campaign UUID
   */
  clearLocalSettings(campaignId: string): void {
    try {
      const key = this.localStoragePrefix + campaignId;
      localStorage.removeItem(key);
      console.log('[CampaignVoiceSettingsService] Cleared localStorage cache');

    } catch (error) {
      console.error('[CampaignVoiceSettingsService] localStorage clear error:', error);
    }
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

/**
 * Singleton instance of CampaignVoiceSettingsService
 * Use this for all campaign voice settings operations
 */
export const campaignVoiceSettingsService = new CampaignVoiceSettingsService();

export default campaignVoiceSettingsService;

